<template>
  <div class="space-y-4 sm:space-y-6">
    <!-- Page Header -->
    <div class="card">
      <div class="card-body">
        <h1
          class="text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text mb-2"
        >
          Modules
        </h1>
        <p class="text-secondary-text dark:text-secondary-dark-text">
          Manage training modules and learning content
        </p>

      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="mt-6 flex flex-col justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
      <span class="text-primary-text dark:text-primary-dark-text">Loading modules...</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <div class="flex items-center">
        <AlertCircle class="h-5 w-5 text-red-500 mr-2" />
        <span class="text-red-700 dark:text-red-300 font-medium">{{ error }}</span>
      </div>
      <button
        @click="fetchModules"
        class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
      >
        Try again
      </button>
    </div>

  <!-- Main Content -->
  <div v-else class="space-y-4 sm:space-y-6">
    <!-- Custom Search and Actions Section -->
    <div class="card">
      <div class="card-body">
        <!-- Header Row with Search and Actions -->
        <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <!-- Search Input -->
          <div class="relative flex-1 max-w-md">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search class="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch(searchQuery)"
              placeholder="Search modules..."
              class="block w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md focus:shadow-md"
            />
            <!-- Clear search button -->
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              title="Clear search"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Action Buttons -->
          <div v-if="userType === 'Admin'" class="flex items-center gap-2">
            <BaseTooltip
              text="Sync module time settings to schedule start and end dates"
              position="top"
            >
              <template #trigger>
                <button
                  @click="showSyncModal = true"
                  class="btn btn-secondary btn-sm flex items-center gap-2"
                >
                  <RefreshCw class="h-4 w-4" />
                  <span class="hidden sm:inline">Sync</span>
                </button>
              </template>
            </BaseTooltip>
            <button
              @click="showAddModal = true"
              class="btn btn-primary btn-sm flex items-center gap-2"
            >
              <Plus class="h-4 w-4" />
              <span class="hidden sm:inline">Add Module</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Ordering Information -->
    <div
      v-if="hasAnyTimeSettings"
      class="card"
    >
      <div class="card-body">
        <div class="flex items-center gap-2 text-sm text-gray-900 dark:text-blue-300">
          <BaseTooltip
            text="Green badge: module has synced time settings (scheduled start and end dates)"
            position="right"
          >
            <template #trigger>
              <span class="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex-shrink-0 mr-1 cursor-help">
                <Calendar class="h-5 w-5" />
              </span>
            </template>
          </BaseTooltip>
          <span>
            Modules are ordered by their assigned time settings. A green badge indicates a module
            has been synced and has scheduled start/end dates; modules without time settings appear
            at the end.
          </span>
        </div>
      </div>
    </div>

    <!-- Modules Data Table -->
    <DataTable
      :data="sortedModulesForDataTable"
      :columns="tableColumns"
      :loading="loading"
      :mobile-columns="mobileColumns"
      item-key="module_id"
      item-label="modules"
      search-placeholder="Search modules..."
      :show-search="false"
      :show-filters="false"
      :show-search-section="false"
      :show-pagination="true"
      :show-summary="true"
      :items-per-page="itemsPerPage"
      :items-per-page-options="[5, 10, 25, 50]"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    >
      <!-- Actions moved to header: search left, buttons right -->

      <!-- Module name with time setting cell -->
      <template #cell-module="{ item }">
        <div class="space-y-1">
          <div class="flex items-center gap-1 min-w-0">
            <span 
              v-if="item.time_setting" 
              class="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex-shrink-0 mr-1"
              :title="`Scheduled: ${formatDateRange(item.time_setting.start_date, item.time_setting.end_date)}`"
            >
              <Calendar class="h-3 w-3" />
            </span>
            <span class="font-medium break-words flex-1 min-w-0">{{ formatModuleName(item) }}</span>
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
            {{ item.description || 'No description available' }}
          </div>
          <div
            v-if="item.time_setting"
            class="text-xs text-blue-600 dark:text-blue-400 mt-1"
          >
            {{
              formatDateRange(
                item.time_setting.start_date,
                item.time_setting.end_date
              )
            }}
          </div>
        </div>
      </template>

      <!-- File column cell -->
      <template #cell-file="{ item }">
        <div class="max-w-full">
          <BaseTooltip text="Click to view details" position="left">
            <template #trigger>
              <span 
                class="cursor-pointer hover:underline transition-colors duration-200 block truncate"
                :class="hasFile(item.material_filepath) ? 'text-blue-700 dark:text-blue-500 hover:text-blue-900 hover:dark:text-blue-700' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:dark:text-gray-300'"
                @click="viewModule(item)"
              >
                {{ getFileName(item.material_filepath) }}
              </span>
            </template>
          </BaseTooltip>
        </div>
      </template>

      <!-- Status column cell -->
      <template #cell-status="{ item }">
        <div class="flex justify-center">
          <BaseTooltip
            :text="getStatusFormatted(item.status) === 'Active' ? 'This module is currently active and accessible to employees' : 'This module is currently inactive and not accessible to employees'"
            position="left"
          >
            <template #trigger>
              <span
                :class="[
                  'px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-help',
                  getStatusFormatted(item.status) === 'Active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                ]"
              >
                {{ getStatusFormatted(item.status) }}
              </span>
            </template>
          </BaseTooltip>
        </div>
      </template>

      <!-- Row actions (Admin Only) -->
      <template #row-actions="{ item }" v-if="userType === 'Admin'">
        <div class="flex justify-center space-x-2 sm:space-x-3">
          <!-- Status Toggle Icon Button -->
          <BaseTooltip
            :text="getStatusFormatted(item.status) === 'Active' ? 'Deactivate' : 'Activate'"
            position="top"
          >
            <template #trigger>
              <button
                :class="[
                  'transition duration-200 transform',
                  getStatusFormatted(item.status) === 'Active'
                    ? 'text-secondary dark:text-secondary-dark dark:hover:text-gray-500 hover:text-gray-900'
                    : 'text-primary dark:text-primary-dark hover:text-gray-900 dark:hover:text-gray-500'
                ]"
                @click="toggleModuleStatus(item)"
              >
                <LockOpen
                  v-if="getStatusFormatted(item.status) === 'Active'"
                  class="h-5 w-5"
                />
                <Lock v-else class="h-5 w-5" />
              </button>
            </template>
          </BaseTooltip>

          <!-- Edit Button -->
          <BaseTooltip
            text="Edit module"
            position="top"
          >
            <template #trigger>
              <button
                class="text-blue-500 hover:text-blue-700 transition"
                @click="editModule(item)"
              >
                <Edit class="h-5 w-5" />
              </button>
            </template>
          </BaseTooltip>

          <!-- Delete Icon Button -->
          <BaseTooltip
            text="Delete module"
            position="top"
          >
            <template #trigger>
              <button
                class="text-red-500 hover:text-red-700 transition"
                @click="deleteModule(item)"
              >
                <Trash2 class="h-5 w-5" />
              </button>
            </template>
          </BaseTooltip>
        </div>
      </template>

      <!-- Mobile actions (Admin Only) -->
      <template #mobile-actions="{ item }" v-if="userType === 'Admin'">
        <div class="flex justify-end space-x-3">
          <!-- Status Toggle -->
          <button
            :class="[
              'transition duration-200 transform flex items-center gap-1 px-2 py-1 rounded text-sm',
              getStatusFormatted(item.status) === 'Active'
                ? 'text-secondary dark:text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
            @click="toggleModuleStatus(item)"
            :title="
              getStatusFormatted(item.status) === 'Active'
                ? 'Deactivate Module'
                : 'Activate Module'
            "
          >
            <LockOpen
              v-if="getStatusFormatted(item.status) === 'Active'"
              class="h-4 w-4"
            />
            <Lock v-else class="h-4 w-4" />
            <span class="hidden sm:inline">
              {{ getStatusFormatted(item.status) === 'Active' ? 'Deactivate' : 'Activate' }}
            </span>
          </button>

          <!-- Edit Button -->
          <button
            class="text-blue-500 hover:text-blue-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
            @click="editModule(item)"
            title="Edit Module"
          >
            <Edit class="h-4 w-4" />
            <span class="hidden sm:inline">Edit</span>
          </button>

          <!-- Delete Button -->
          <button
            class="text-red-500 hover:text-red-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
            @click="deleteModule(item)"
            title="Delete Module"
          >
            <Trash2 class="h-4 w-4" />
            <span class="hidden sm:inline">Delete</span>
          </button>
        </div>
      </template>

      <!-- Custom mobile cell for module (to show time setting info) -->
      <template #mobile-module="{ item }">
        <div class="flex flex-col">
          <!-- Name (top) - right aligned -->
          <div class="mb-1 w-full min-w-0 flex justify-end">
            <span class="text-base font-semibold text-gray-900 dark:text-white break-words block line-clamp-2 text-right max-w-[85%]">
              {{ formatModuleName(item) }}
            </span>
          </div>

          <!-- Description (middle) - right aligned -->
          <div class="w-full mb-1 flex justify-end">
            <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 text-right max-w-[75%] min-w-0">
              {{ item.description || 'No description available' }}
            </p>
          </div>

          <!-- Time setting / Date (bottom) - right aligned -->
          <div v-if="item.time_setting" class="w-full flex justify-end">
            <div class="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
              <span class="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex-shrink-0">
                <Calendar class="h-3 w-3" />
              </span>
              <span class="text-xs truncate max-w-[65%] min-w-0 text-right">
                {{ formatDateRange(item.time_setting.start_date, item.time_setting.end_date) }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- Custom mobile cell for file -->
      <template #mobile-file="{ item }">
        <div class="max-w-[60%] text-right min-w-0">
          <BaseTooltip text="Click to view details" position="top">
            <template #trigger>
              <span
                class="cursor-pointer hover:underline transition-colors duration-200 block break-words line-clamp-2"
                :class="hasFile(item.material_filepath) ? 'text-blue-700 dark:text-blue-500 hover:text-blue-900 hover:dark:text-blue-700' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:dark:text-gray-300'"
                @click="viewModule(item)"
              >
                {{ getFileName(item.material_filepath) }}
              </span>
            </template>
          </BaseTooltip>
        </div>

      </template>

      <!-- Empty actions -->
      <template #empty-actions>
        <button
          v-if="userType === 'Admin'"
          @click="showAddModal = true"
          class="btn btn-primary btn-sm flex items-center gap-2 justify-center"
        >
          <Plus class="h-4 w-4" />
          Create Module
        </button>
      </template>
    </DataTable>
  </div>

    <!-- Create Module Modal (Admin Only) -->
    <CreateModule
      :is-visible="showAddModal"
      @close="showAddModal = false"
      @success="onModuleCreated"
    />

    <!-- Edit Module Modal (Admin Only) -->
    <EditModule
      :is-visible="showEditModal"
      :module="selectedModule || undefined"
      @close="showEditModal = false"
      @success="onModuleUpdated"
    />

    <!-- Sync Modules Modal (Admin Only) -->
    <CalibrateModules
      :is-visible="showSyncModal"
      @close="showSyncModal = false"
      @success="onSyncSuccess"
    />

    <!-- View Module Modal -->
    <ViewModule
      :is-visible="showViewModal"
      :module="selectedModule"
      @close="showViewModal = false"
    />

    <!-- Delete Module Modal (Admin Only) -->
    <DeleteModule
      :is-visible="showDeleteModal"
      :module="moduleToDelete"
      @close="onDeleteModalClose"
      @success="onModuleDeleteSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useToastService } from '@/services/toast'
import CreateModule from '@/components/modals/CreateModule.vue'
import CalibrateModules from '@/components/modals/CalibrateModules.vue'
import DataTable from '@/components/common/DataTable.vue'
import EditModule from '@/components/modals/EditModule.vue'
import ViewModule from '@/components/modals/ViewModule.vue'
import ModulesService, { type Module } from '@/services/modules'
import DeleteModule from '@/components/modals/DeleteModule.vue'
import BaseTooltip from '@/components/common/BaseTooltip.vue'
import {
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Lock,
  LockOpen,
  RefreshCw,
  Calendar,
  Search,
  X
} from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Empathy Health Services, Inc.')
useTitle(`Modules - ${appTitle.value}`)

// Get user information
const { userType } = useUser()

// Toast service for notifications
const toast = useToastService()

// Reactive state
const loading = ref(false)
const error = ref<string | null>(null)
const searchQuery = ref('')
const modules = ref<Module[]>([])
const showAddModal = ref(false)
const showSyncModal = ref(false)
const showViewModal = ref(false)
const selectedModule = ref<Module | null>(null)
const showDeleteModal = ref(false)
const moduleToDelete = ref<Module | null>(null)

// Pagination state
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Computed properties
const filteredModules = computed(() => {
  if (!searchQuery.value) return modules.value

  return modules.value.filter(module => {
    const name = module.name || ''
    const description = module.description || ''
    const fileName = getFileName(module.material_filepath) || ''
    const formattedName = formatModuleName(module) // Include formatted name (MMYY-HHA-Title)
    return (
      name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      fileName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      formattedName.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })
})

// Pre-sorted data for DataTable that maintains our custom sorting logic
const sortedModulesForDataTable = computed(() => {
  const sorted = [...filteredModules.value] // Use filtered modules instead of all modules

  // Sort modules by their assigned order: time settings first, then by module_id
  sorted.sort((a, b) => {
    // Modules with time settings come first, ordered by start_date
    if (a.time_setting && b.time_setting) {
      const aStartDate = new Date(a.time_setting.start_date).getTime()
      const bStartDate = new Date(b.time_setting.start_date).getTime()

      if (aStartDate !== bStartDate) {
        return aStartDate - bStartDate // Always ascending by start date
      } else {
        // If start dates are the same, fall back to module_id for consistent ordering
        return a.module_id - b.module_id
      }
    }

    // Handle modules with time settings vs without
    if (a.time_setting && !b.time_setting) {
      return -1 // Modules with time settings come first
    }
    if (!a.time_setting && b.time_setting) {
      return 1 // Modules without time settings come last
    }

    // For modules without time settings, order by module_id
    return a.module_id - b.module_id
  })

  return sorted
})

// Check if any modules have time settings to show indicator
const hasAnyTimeSettings = computed(() => modules.value.some(module => module.time_setting))

// DataTable Configuration
const tableColumns = [
  {
    key: 'module',
    label: 'Module',
    sortable: false
  },
  {
    key: 'file',
    label: 'File',
    sortable: false
  },
  {
    key: 'status',
    label: 'Status',
    sortable: false,
    align: 'center' as const
  }
]

const mobileColumns = [
  {
    key: 'module',
    label: 'Module',
    primary: true
  },
  {
    key: 'file',
    label: 'File',
    secondary: true
  }
]

// DataTable event handlers
const handlePageChange = (page: number) => {
  currentPage.value = page
}

const handleItemsPerPageChange = (newItemsPerPage: number) => {
  itemsPerPage.value = newItemsPerPage
  currentPage.value = 1 // Reset to first page
}

// Helper functions
const formatDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate).toLocaleDateString()
  const end = new Date(endDate).toLocaleDateString()
  return `${start} - ${end}`
}

const formatModuleName = (module: Module): string => {
  // Remove "Module X -" pattern from the name (where X is any number)
  let cleanedName = module.name.replace(/^Module\s+\d+\s*-\s*/i, '').trim()
  
  if (!module.time_setting || !module.time_setting.start_date) {
    return cleanedName
  }
  
  const startDate = new Date(module.time_setting.start_date)
  const month = String(startDate.getMonth() + 1).padStart(2, '0') // getMonth() returns 0-11
  const year = String(startDate.getFullYear()).slice(-2) // Get last 2 digits of year
  
  return `${month}${year}-HHA-${cleanedName}`
}

// Methods
const fetchModules = async () => {
  loading.value = true
  error.value = null

  try {
    const modulesList = await ModulesService.getModules()
    // console.log('Modules fetched successfully:', modulesList)
    modules.value = modulesList
  } catch (err: any) {
    console.error('Failed to fetch modules:', err)
    error.value = 'Failed to fetch modules. Please try again.'
    modules.value = [] // Remove dummy data
  } finally {
    loading.value = false
  }
}

const onModuleCreated = () => {
  // Refresh the modules list when a new module is created
  fetchModules()
  // Close the modal
  showAddModal.value = false
}

const onSyncSuccess = (message: string) => {
  // Show success toast notification
  toast.success('Sync Successful', message)
  // Refresh the modules list to show updated time settings
  fetchModules()
  // Close the modal
  showSyncModal.value = false
}

const getFileName = (filePath: string | null | undefined): string => {
  if (!filePath) return '(No file uploaded)'
  const pathParts = filePath.split('/')
  return pathParts[pathParts.length - 1] || '(No file uploaded)'
}

const hasFile = (filePath: string | null | undefined): boolean => {
  return !!(filePath && filePath.trim())
}

const getStatusFormatted = (status: string | number | null | undefined): string => {
  if (status === null || status === undefined) return 'Inactive'
  const statusNum = Number(status)
  if (statusNum === 1) return 'Active'
  return 'Inactive'
}

// Search and filter methods
const handleSearch = (query: string) => {
  // Update our search query which will trigger the filteredModules computed property
  searchQuery.value = query || ''
  currentPage.value = 1 // Reset to first page when searching
}

const clearSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
}

// Action methods
const viewModule = (module: Module) => {
  selectedModule.value = module
  showViewModal.value = true
}

const loadingToggle = ref(false)
const toggleInProgress = ref<number | null>(null)

// Updated toggle function
const toggleModuleStatus = async (module: Module) => {
  try {
    toggleInProgress.value = module.module_id
    loadingToggle.value = true

    const newStatus = module.status === 1 ? 0 : 1
    const action = newStatus === 1 ? 'Activated' : 'Deactivated'

    const response = await ModulesService.updateModuleStatus(module.module_id, newStatus as 0 | 1)
    
    if (response.success) {
      // Update local state only if API call succeeds
      const moduleIndex = modules.value.findIndex(m => m.module_id === module.module_id)
      if (moduleIndex !== -1) {
        modules.value[moduleIndex].status = newStatus
      }
      toast.success(`Module "${module.name}" has been ${action}.`)
    } else {
      toast.error(response.message || 'Failed to update module status.')
    }
  } catch (error: any) {
    console.error('Error toggling module status:', error)
    const errorMessage = error.message || 'Failed to update module status. Please try again.'
    toast.error(errorMessage)
  } finally {
    loadingToggle.value = false
    toggleInProgress.value = null
  }
}

// For Edit Module
const showEditModal = ref(false)

const editModule = (module: Module) => {
  selectedModule.value = module
  showEditModal.value = true
}

const onModuleUpdated = async () => {
  // Store the current module ID before refreshing
  const currentModuleId = selectedModule.value?.module_id
  
  // Refresh the modules list when a module is updated
  await fetchModules()
  
  // Close the edit modal
  showEditModal.value = false
  
  // If ViewModule was open, update selectedModule with fresh data
  if (currentModuleId && showViewModal.value) {
    const updatedModule = modules.value.find(m => m.module_id === currentModuleId)
    if (updatedModule) {
      selectedModule.value = updatedModule
    }
  } else {
    // Reset selected module if ViewModule is not open
    selectedModule.value = null
  }

  // Show success notification
  toast.success('Module has been updated successfully')
}

// Delete Module
const deleteModule = (module: Module) => {
  moduleToDelete.value = module
  showDeleteModal.value = true
}

const onModuleDeleteSuccess = () => {
  fetchModules()
  showDeleteModal.value = false
  moduleToDelete.value = null
}

const onDeleteModalClose = () => {
  showDeleteModal.value = false
  moduleToDelete.value = null
}

// Lifecycle
onMounted(() => {
  fetchModules()
})
</script>

<style scoped>
.card {
  background-color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

.card-body {
  padding: 1.5rem;
}

.btn {
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-hover);
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #4b5563;
}

.btn-outline:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.dark .btn-outline {
  color: #9ca3af;
  border-color: #4b5563;
}

.dark .btn-outline:hover:not(:disabled) {
  background-color: #374151;
  border-color: #6b7280;
}

.btn-sm {
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

/* Line clamp utilities for text truncation */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom table column widths for responsive design */
:deep(.overflow-x-auto) {
  overflow-x: auto;
  min-width: 100%;
}

:deep(.min-w-full) {
  table-layout: fixed;
  width: 100%;
}

:deep(.min-w-full th:nth-child(1)) {
  width: 35%; /* Module column - slightly reduced */
  min-width: 140px;
}

:deep(.min-w-full th:nth-child(2)) {
  width: 25%; /* File column - reduced for less overflow */
  min-width: 100px;
  max-width: 200px;
}

:deep(.min-w-full th:nth-child(3)) {
  width: 15%; /* Status column - fixed small space */
  min-width: 80px;
  max-width: 100px;
}

:deep(.min-w-full th:nth-child(4)) {
  width: 10%; /* Actions column - slightly increased */
  min-width: 120px;
}

/* Ensure table cells respect column widths */
:deep(.min-w-full td:nth-child(1)) {
  width: 35%;
  min-width: 140px;
}

:deep(.min-w-full td:nth-child(2)) {
  width: 25%;
  min-width: 100px;
  max-width: 200px;
}

:deep(.min-w-full td:nth-child(3)) {
  width: 15%;
  min-width: 80px;
  max-width: 100px;
}

:deep(.min-w-full td:nth-child(4)) {
  width: 10%;
  min-width: 120px;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 1536px) {
  :deep(.min-w-full th:nth-child(2)),
  :deep(.min-w-full td:nth-child(2)) {
    max-width: 150px;
  }
}

@media (max-width: 1280px) {
  :deep(.min-w-full th:nth-child(2)),
  :deep(.min-w-full td:nth-child(2)) {
    max-width: 120px;
  }
}

/* Force DataTable to show mobile/card layout at screens <= 1536px for ModuleView */
@media (max-width: 1536px) {
  /* Hide the desktop table container inside the DataTable component */
  :deep(.overflow-x-auto) {
    display: none !important;
  }

  /* Ensure the mobile card container is visible */
  :deep(.xl\:hidden) {
    display: block !important;
  }
}
</style>
